import { useEffect, useMemo, useState } from 'react';

import CopyActions from './CopyActions';
import CopyHeader from './CopyHeader';
import CopyHistory from './CopyHistory';
import CopyNotices from './CopyNotices';
import { LintIssue, lintVariant } from './copyLint';
import { summarizeProblems } from './copyProblems';
import {
  editableFieldsOf,
  isEmptySpanish,
  requiresOf,
  variantNames,
} from './copyModel';
import CopyPreview, { PreviewMode } from './CopyPreview';
import FieldEditor from './FieldEditor';
import SpanishEmpty from './SpanishEmpty';
import {
  Body,
  Caption,
  Columns,
  EditorCard,
  FieldsColumn,
  Label,
  Locked,
  Notice,
  PlainText,
  PreviewColumn,
  Segment,
  SmallText,
  VariantBox,
  WrapRow,
} from './styles';
import useCopyMessage from './useCopyMessage';
import Pill from '../shared/Pill';
import SectionState from '../shared/SectionState';
import { formatDateTime } from '../shared/format';
import { CopyParams } from '../useLifecycleUrlState';

import ConfirmationModal from '@/components/ConfirmationModalV2';
import useAuth from '@/hooks/useAuth';
import { CopyDetail, CopyLocale } from '@/services/lifecycleAPI';

type Props = {
  stepKey: string;
  locale: CopyLocale;
  view: CopyParams['view'];
  onView: (view: CopyParams['view']) => void;
  onSaved: () => void;
  onDirty: (dirty: boolean) => void;
};

const SAVED_NOTICE =
  'Draft saved. Everyone with the tenant admin role sees the same draft.';

const CopyEditor = ({
  stepKey,
  locale,
  view,
  onView,
  onSaved,
  onDirty,
}: Props) => {
  const { currentAdminIsTenantAdminRole } = useAuth();
  const message = useCopyMessage(stepKey, locale);
  const { detail, text } = message;
  const [variantPick, setVariantPick] = useState<string | null>(null);
  const [previewMode, setPreviewMode] = useState<PreviewMode>('sample');
  const [notice, setNotice] = useState<string | null>(null);
  const [revertPending, setRevertPending] = useState<number | null>(null);

  useEffect(() => {
    onDirty(message.dirty);
    return () => onDirty(false);
  }, [message.dirty, onDirty]);

  const names = useMemo(() => (detail ? variantNames(detail) : []), [detail]);
  const variant =
    variantPick && names.includes(variantPick) ? variantPick : names[0];

  const issues = useMemo(() => {
    const result: Record<string, LintIssue[]> = {};
    if (!detail) return result;
    names.forEach((name) => {
      result[name] = lintVariant({
        text: text[name] ?? {},
        allowed: detail.allowed_fields[name] ?? {},
        sampleValues: detail.sample_values,
        stopLine: detail.locked.sms_stop_line,
        locale,
      });
    });
    return result;
  }, [detail, names, text, locale]);

  if (message.loading) {
    return (
      <EditorCard data-testid="copyEditor" aria-label="Copy editor">
        <SectionState mode="loading" title="message" />
      </EditorCard>
    );
  }

  if (message.notFound) {
    return (
      <EditorCard data-testid="copyEditor" aria-label="Copy editor">
        <SectionState
          mode="empty"
          title="Not found"
          caption={`There is no message "${stepKey}". Pick one from the list.`}
        />
      </EditorCard>
    );
  }

  if (message.error || !detail || !variant) {
    return (
      <EditorCard data-testid="copyEditor" aria-label="Copy editor">
        <SectionState
          mode="error"
          error={message.error ?? 'Could not load this message.'}
          onRetry={message.retry}
        />
      </EditorCard>
    );
  }

  const draft = detail.draft;
  const allIssues = names.flatMap((name) => issues[name]);
  const { problemTitle, listed } = summarizeProblems(
    detail,
    names,
    allIssues,
    message.saveError,
  );

  const fields = editableFieldsOf(detail, variant);
  const requires = requiresOf(detail, variant);
  const hasSms = names.some((n) => detail.editable_fields[n]?.includes('sms'));
  const hasEmail = names.some((n) =>
    detail.editable_fields[n]?.includes('body'),
  );
  const saveDisabled =
    !currentAdminIsTenantAdminRole ||
    message.saving ||
    !(message.dirty || message.staleDraft) ||
    allIssues.some((i) => i.blocksSave);

  const run = async (
    action: Promise<CopyDetail | null>,
    success: string | ((result: CopyDetail) => string),
  ) => {
    const result = await action;
    if (!result) return;
    setNotice(typeof success === 'function' ? success(result) : success);
    onSaved();
  };

  const revertTo = (version: number) =>
    run(
      message.revert(version),
      (d) =>
        `Reverted to version ${version}. Published as version ${d.version}.`,
    );

  return (
    <EditorCard data-testid="copyEditor" aria-label="Copy editor">
      <CopyHeader detail={detail} view={view} onView={onView} />

      <Body style={{ paddingBottom: 0 }}>
        <CopyNotices
          conflict={message.conflict}
          draftConflict={message.draftConflict}
          notice={notice}
          error={message.saveError}
          showError={view === 'history'}
          onReload={message.retry}
        />
      </Body>
      {view === 'history' ? (
        <CopyHistory
          detail={detail}
          readOnly={!currentAdminIsTenantAdminRole}
          busy={message.saving}
          onRevert={(version) =>
            message.dirty ? setRevertPending(version) : revertTo(version)
          }
        />
      ) : isEmptySpanish(detail) ? (
        <SpanishEmpty
          readOnly={!currentAdminIsTenantAdminRole}
          busy={message.saving}
          error={message.saveError}
          onStart={() =>
            run(
              message.startSpanish(),
              'Spanish draft started from the English text.',
            )
          }
        />
      ) : (
        <Body>
          <Notice tone="green">
            <span>
              Applies to messages sent after you publish, including ones already
              scheduled.
            </span>
            {hasSms && (
              <span>
                SMS: keep the meaning of the registered sample. Big changes need
                a campaign update.
              </span>
            )}
          </Notice>
          {draft && (
            <WrapRow>
              <Pill tone="amber">Draft</Pill>
              <PlainText>
                {`Saved by ${draft.actor_name ?? 'a tenant admin'}, ${formatDateTime(draft.updated_at)}. Anyone with the tenant admin role edits this same draft.`}
              </PlainText>
            </WrapRow>
          )}
          {draft && message.staleDraft && (
            <Notice tone="red" data-testid="copyStaleDraft">
              {`This draft was saved on version ${draft.base_version}. Check it, then publish.`}
            </Notice>
          )}
          {(problemTitle || listed.length > 0) && (
            <Notice tone="red" role="alert" data-testid="copyProblems">
              {problemTitle && <strong>{problemTitle}</strong>}
              {listed.map((m) => (
                <SmallText key={m}>{m}</SmallText>
              ))}
            </Notice>
          )}
          <VariantBox>
            <Label>Variant</Label>
            <Segment role="group" aria-label="Variant">
              {names.map((name) => (
                <button
                  key={name}
                  type="button"
                  data-testid={`copyVariant-${name}`}
                  aria-pressed={name === variant}
                  onClick={() => setVariantPick(name)}
                >
                  {issues[name].length > 0 ? `${name} ●` : name}
                </button>
              ))}
            </Segment>
            <Caption>
              {requires.length > 0
                ? `Used only when: ${requires.join(', ')}. `
                : ''}
              The engine uses the first variant whose fields it can fill for the
              worker. ● = this variant has a problem.
            </Caption>
          </VariantBox>

          <Columns>
            <FieldsColumn>
              {fields.map((field) => (
                <FieldEditor
                  key={`${variant}/${field}`}
                  field={field}
                  value={text[variant]?.[field] ?? ''}
                  changed={
                    (text[variant]?.[field] ?? '') !==
                    String(detail.current[variant]?.[field] ?? '')
                  }
                  allowed={detail.allowed_fields[variant]?.[field] ?? []}
                  issues={issues[variant]
                    .filter((i) => i.field === field)
                    .map((i) => i.message)}
                  readOnly={!currentAdminIsTenantAdminRole}
                  curlyFixed={message.curlyFixed(variant, field)}
                  longestValues={detail.sample_values}
                  stopLine={detail.locked.sms_stop_line}
                  locale={locale}
                  stopLineAdded={
                    !String(detail.default[variant]?.[field] ?? '').includes(
                      detail.locked.sms_stop_line,
                    )
                  }
                  onChange={(raw) => {
                    setNotice(null);
                    message.edit(variant, field, raw);
                  }}
                />
              ))}
              {hasEmail && (
                <Locked>{`Email footer: "${detail.locked.email_footer}" Not editable.`}</Locked>
              )}
            </FieldsColumn>
            <PreviewColumn>
              <CopyPreview
                text={text[variant] ?? {}}
                mode={previewMode}
                onMode={setPreviewMode}
                longestValues={detail.sample_values}
                emailFooter={detail.locked.email_footer}
              />
            </PreviewColumn>
          </Columns>

          <CopyActions
            label={detail.label}
            readOnly={!currentAdminIsTenantAdminRole}
            busy={message.saving}
            dirty={message.dirty}
            hasDraft={Boolean(draft)}
            hasProblems={problemTitle !== null}
            saveBlocked={saveDisabled}
            onSave={() => run(message.save(), SAVED_NOTICE)}
            onPublish={() =>
              run(
                message.publish(() => {
                  setNotice(SAVED_NOTICE);
                  onSaved();
                }),
                (d) =>
                  `Published version ${d.version}. Messages sent from now on use it, including scheduled ones.`,
              )
            }
            onDiscard={() => run(message.discard(), 'Draft discarded.')}
            onReset={() =>
              run(
                message.reset(),
                (d) =>
                  `Reset to the default text. Published as version ${d.version}.`,
              )
            }
          />
        </Body>
      )}
      {revertPending !== null && (
        <ConfirmationModal
          disableClickout
          title="Discard unsaved changes?"
          message="Your edits to this message are not saved."
          subMessage={null}
          confirmLabel="Discard"
          cancelLabel="Keep editing"
          onCancel={() => setRevertPending(null)}
          onConfirm={() => {
            const version = revertPending;
            setRevertPending(null);
            revertTo(version);
          }}
        />
      )}
    </EditorCard>
  );
};

export default CopyEditor;
